import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  useTheme
} from "@mui/material";
import {
  Business,
  Campaign,
  Event,
  Visibility,
  TrendingUp,
  ContactMail
} from "@mui/icons-material";
import API_BASE_URL from "../apiConfig";
import MKBox from "../components/MKBox";
import MKButton from "../components/MKButton";
import MKTypography from "../components/MKTypography";

function SponsorPage() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    business: "",
    email: "",
    description: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    if (!formData.business.trim()) {
      setError("Business name is required");
      return;
    }

    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }

    if (!formData.description.trim()) {
      setError("Description is required");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sponsors/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send sponsorship inquiry");
      }

      setSuccess("Thank you for your interest! We'll get back to you soon.");
      setFormData({
        name: "",
        business: "",
        email: "",
        description: ""
      });
    } catch (error) {
      console.error("Submit error:", error);
      setError(error.message || "Failed to send sponsorship inquiry");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Visibility sx={{ fontSize: 40 }} />,
      title: "High Visibility",
      description: "Reach thousands of gaming enthusiasts across San Diego with prominent ad placement.",
      color: "#2196f3"
    },
    {
      icon: <Event sx={{ fontSize: 40 }} />,
      title: "Event Sponsorship",
      description: "Sponsor tournaments, meetups, and community events with your brand.",
      color: "#f50057"
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Targeted Audience",
      description: "Connect with engaged gamers who are passionate about your products and services.",
      color: "#4caf50"
    },
    {
      icon: <Campaign sx={{ fontSize: 40 }} />,
      title: "Custom Campaigns",
      description: "Create tailored marketing campaigns that resonate with our gaming community.",
      color: "#ff9800"
    }
  ];

  return (
    <Box sx={{ minHeight: "100vh", py: 8 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <MKTypography
            variant="h2"
            fontWeight="bold"
            mb={3}
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              color: '#e91e63'
            }}
          >
            Sponsor SD Gaming
          </MKTypography>
          <MKTypography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
          >
            Partner with San Diego's premier gaming community platform and reach thousands of engaged gamers
          </MKTypography>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <MKTypography
              variant="h3"
              fontWeight="bold"
              mb={2}
              sx={{ color: '#e91e63' }}
            >
              Why Sponsor With Us?
            </MKTypography>
            <MKTypography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Connect with a passionate gaming community and grow your brand
            </MKTypography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `${benefit.color}15`,
                        color: benefit.color,
                        mb: 3
                      }}
                    >
                      {benefit.icon}
                    </Box>
                    <MKTypography
                      variant="h6"
                      fontWeight="bold"
                      mb={2}
                      sx={{ color: '#e91e63' }}
                    >
                      {benefit.title}
                    </MKTypography>
                    <MKTypography
                      variant="body2"
                      color="text.secondary"
                    >
                      {benefit.description}
                    </MKTypography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Sponsorship Opportunities */}
        <Box sx={{ mb: 8 }}>
          <Paper
            sx={{
              p: { xs: 4, md: 6 },
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Business sx={{ fontSize: 60, mb: 2, opacity: 0.8, color: 'white !important' }} />
              <MKTypography
                variant="h3"
                fontWeight="bold"
                mb={3}
                sx={{ color: 'white !important' }}
              >
                Sponsorship Opportunities
              </MKTypography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <MKTypography variant="h5" fontWeight="bold" mb={3} sx={{ color: 'white !important' }}>
                  Event Sponsorship
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  • Tournament sponsorships with logo placement and announcements
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  • Community meetup sponsorships
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  • Exclusive event branding opportunities
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  • Product placement and sampling at events
                </MKTypography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <MKTypography variant="h5" fontWeight="bold" mb={3} sx={{ color: 'white !important' }}>
                  Digital Advertising
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  • Banner ads on homepage and club pages
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  • Featured sponsor sections
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  • Newsletter sponsorships
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  • Social media promotion packages
                </MKTypography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Contact Form */}
        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <ContactMail sx={{ 
              fontSize: 60, 
              mb: 2, 
              color: theme.palette.mode === 'dark' ? 'white !important' : '#e91e63 !important' 
            }} />
            <MKTypography
              variant="h3"
              fontWeight="bold"
              mb={2}
              sx={{ color: '#e91e63' }}
            >
              Get In Touch
            </MKTypography>
            <MKTypography
              variant="h6"
              color="text.secondary"
            >
              Ready to partner with us? Fill out the form below and we'll get back to you soon.
            </MKTypography>
          </Box>

          <Card>
            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Business Name"
                      value={formData.business}
                      onChange={(e) => handleInputChange('business', e.target.value)}
                      placeholder="Enter your business name"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email address"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Tell us about your sponsorship interests, budget, and goals"
                      multiline
                      rows={4}
                      required
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <MKBox sx={{ display: "flex", justifyContent: "center" }}>
                  <MKButton
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      bgcolor: "#000000",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      textTransform: "none",
                      px: 6,
                      py: 1.5,
                      "&:hover": {
                        bgcolor: "#333333"
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Send Inquiry"}
                  </MKButton>
                </MKBox>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}

export default SponsorPage; 